import type { ReactNode } from "react";
import { CheckCircleIcon } from "@/icons/CheckCircleIcon";
import { MapPinIcon } from "@/icons/MapPinIcon";
import { UsersIcon } from "@/icons/UsersIcon";
import { CalendarIcon } from "@/icons/CalendarIcon";
import type { DashboardSetup } from "@/domain/dashboard/types";
import styles from "./DashboardActivation.module.css";

interface DashboardActivationProps {
  readonly setup: DashboardSetup;
  readonly ownerFirstName: string;
}

interface Step {
  readonly key: keyof DashboardSetup;
  readonly title: string;
  readonly icon: ReactNode;
  readonly href: string | null;
}

const STEPS: readonly Step[] = [
  {
    key: "hasSite",
    title: "Add your first cleaning site",
    icon: <MapPinIcon width={20} height={20} />,
    href: "/sites/new",
  },
  {
    key: "hasCleaner",
    title: "Invite a cleaner",
    icon: <UsersIcon width={20} height={20} />,
    href: "/team?invite=1",
  },
  {
    key: "hasShift",
    title: "Build your first rota",
    icon: <CalendarIcon width={20} height={20} />,
    href: "/rota",
  },
];

function StepItem({ step, done }: { readonly step: Step; readonly done: boolean }) {
  const status = done ? "done" : "pending";
  const content = (
    <>
      <span className={`${styles.stepMarker} ${done ? styles.stepMarkerDone : ""}`}>
        {done ? <CheckCircleIcon width={22} height={22} /> : step.icon}
      </span>
      <span className={styles.stepTitle}>{step.title}</span>
    </>
  );

  return (
    <li data-status={status} className={styles.step}>
      {step.href && !done ? (
        <a href={step.href} className={styles.stepLink}>
          {content}
        </a>
      ) : (
        <span className={styles.stepRow}>{content}</span>
      )}
    </li>
  );
}

export function DashboardActivation({ setup, ownerFirstName }: DashboardActivationProps) {
  const doneCount = STEPS.filter((s) => setup[s.key]).length;

  return (
    <section
      className={styles.wrapper}
      role="region"
      aria-label="Get set up"
    >
      <div className={styles.header}>
        <div className={styles.headingBlock}>
          <h2 className={styles.heading}>Let's get you set up, {ownerFirstName}</h2>
          <p className={styles.subheading}>
            A few quick steps and your live dashboard lights up.
          </p>
        </div>
        <div className={styles.progress} aria-live="polite">
          {doneCount} of {STEPS.length} done
        </div>
      </div>

      <ol className={styles.steps}>
        {STEPS.map((step) => (
          <StepItem key={step.key} step={step} done={setup[step.key]} />
        ))}
      </ol>
    </section>
  );
}
